/**
 * Building Template Types
 *
 * Defines the interface for building templates that generate
 * buildings and units from form parameters.
 */

import type { Database } from '@planmax/shared-types'

import type { LucideIcon } from 'lucide-react'
import type { z } from 'zod'
import type { HangarType } from '@/types/hangars'

/** Template type identifiers */
export type TemplateType =
  | 'single_unit_community'
  | 'single_row'
  | 'double_row'
  | 'outdoor_rectangular'
  | 'outdoor_row'
  | 'outdoor_grid'
  | 'outdoor_polygon'
  | 'standard_t_hangar'
  | 'nested_t_hangar'
  | 'arch_hangar'

/** Door wall position (relative to building orientation) */
export type DoorWall = 'front' | 'back' | 'left' | 'right'

/** Door types — mirrors the DB `door_type` enum (single source of truth). */
export type DoorType = Database['public']['Enums']['door_type']

/** Door configuration for building-level (existing single-unit template) */
export interface DoorConfig {
  wall: DoorWall
  width_ft: number
  height_ft: number
  offset_ft: number
  type: DoorType
}

// ============================================================================
// MULTI-UNIT TEMPLATE TYPES (Single-Row, Double-Row, etc.)
// ============================================================================

/** Door specification for unit edges (multi-unit templates) */
export interface DoorSpec {
  /** Distance from edge start in feet */
  offset_ft: number
  /** Door width in feet */
  width_ft: number
  /** Door height in feet */
  height_ft: number
  /** Door type (all 4 types supported) */
  type: DoorType
}

/** Edge type classification for wall rendering */
export type EdgeType = 'exterior' | 'partition' | 'internal'

/** Edge side (relative to building orientation) */
export type EdgeSide = 'front' | 'back' | 'left' | 'right'

/**
 * Unit edge with classification for rendering and collision
 *
 * Key principle: "Every unit has all its walls. Only one unit RENDERS each shared wall."
 * - Collision uses unit's own edges (no building context needed)
 * - Rendering respects classification (no duplicate walls)
 */
export interface UnitEdge {
  /** Edge side (front, back, left, right) */
  side: EdgeSide
  /** Edge classification */
  type: EdgeType
  /** Whether this edge can be shared with adjacent unit */
  canBeShared: boolean
  /** Neighbor unit ID that shares this edge (set at instantiation) */
  sharedWith?: string | null
  /** Door openings on this edge (multiple doors per edge supported) */
  openings?: DoorSpec[]
}

/**
 * Per-wall door configuration for units
 * - front: Always available (main entrance, faces ramp/taxiway)
 * - back: Always available (rear doors)
 * - left: Only available for first unit in row
 * - right: Only available for last unit in row
 */
export interface UnitDoorsConfig {
  /** Front wall doors (always available - faces ramp/taxiway) */
  front?: DoorSpec[]
  /** Back wall doors (always available - rear of unit) */
  back?: DoorSpec[]
  /** Left wall doors (only valid for first unit in row) */
  left?: DoorSpec[]
  /** Right wall doors (only valid for last unit in row) */
  right?: DoorSpec[]
}

/** Override configuration for individual units in multi-unit templates */
export interface UnitOverrideConfig {
  /** Unit index (0-based) */
  index: number
  /** Override unit type (e.g., convert one bay to storage) */
  type?: UnitType
  /** Override calculated width */
  width_ft?: number
  /** Override default doors (per-wall configuration) */
  doors?: UnitDoorsConfig
}

/** Door type default configurations */
export interface DoorTypeDefaults {
  min_width_ft: number
  max_width_ft: number
  typical_height_ft: number
  clearance_ft: number
  threshold_ft: number
}

/** Door type configuration constants */
export const DOOR_TYPE_DEFAULTS: Record<DoorType, DoorTypeDefaults> = {
  bifold: {
    min_width_ft: 30,
    max_width_ft: 100,
    typical_height_ft: 20,
    clearance_ft: 5,
    threshold_ft: 2,
  },
  sliding: {
    min_width_ft: 40,
    max_width_ft: 200,
    typical_height_ft: 25,
    clearance_ft: 3,
    threshold_ft: 1,
  },
  rolling: {
    min_width_ft: 20,
    max_width_ft: 60,
    typical_height_ft: 18,
    clearance_ft: 2,
    threshold_ft: 1,
  },
  hydraulic: {
    min_width_ft: 50,
    max_width_ft: 150,
    typical_height_ft: 30,
    clearance_ft: 10,
    threshold_ft: 3,
  },
  manual: {
    min_width_ft: 10,
    max_width_ft: 40,
    typical_height_ft: 14,
    clearance_ft: 2,
    threshold_ft: 1,
  },
}

/** Generated building configuration for 3D rendering and database */
export interface GeneratedBuilding {
  name: string
  code: string
  type: HangarType
  exterior_width: number // X-axis (length)
  exterior_depth: number // Y-axis (depth/width)
  exterior_height: number
  latitude: number
  longitude: number
  rotation: number // Radians
  door_width: number
  door_height: number
  settings: {
    floor_color: string | null
    wall_color: string
    doors?: DoorConfig[]
    border_color?: string
    border_style?: 'solid' | 'dashed'
    border_width_ft?: number
    floor_opacity?: number
  }
  type_config: Record<string, unknown>
}

/** Unit type for templates - uses canonical UnitType from types */
export type { UnitType } from '@/types/units'
import type { UnitType } from '@/types/units'

/** Generated unit configuration */
export interface GeneratedUnit {
  /** Database ID (optional, only present for saved units) */
  id?: string
  code: string
  type: UnitType
  capacity_strategy: 'single' | 'stackable' | 'area_based'
  interior_width: number
  interior_depth: number
  interior_height: number
  door_width: number | null
  door_height: number | null
  door_type?: DoorType
  offset_x: number
  offset_y: number
  unit_rotation: number
  /**
   * Edge classification for multi-unit templates
   * Used for rendering (avoid duplicate walls) and collision detection
   */
  edges?: UnitEdge[]
  /**
   * T-hangar specific edges (8 edges per unit instead of 4)
   * Used by standard_t_hangar and nested_t_hangar templates
   */
  tHangarEdges?: import('./standard-t-hangar/edges').THangarUnitEdge[]
}

/** Base template parameters that all templates share */
export interface BaseTemplateParams {
  name: string
  code: string
  latitude: number
  longitude: number
  bearing_deg: number
  floor_color: string
  wall_color: string
}

/**
 * A building template with its concrete params type erased.
 *
 * `BuildingTemplate<TParams>` is contravariant in `TParams` (generate/validate accept
 * it), so `BuildingTemplate<SingleRowParams>` is NOT assignable to
 * `BuildingTemplate<BaseTemplateParams>` — the registry below genuinely holds
 * templates with differing param types, so it stores them type-erased instead of
 * asserting an unsound narrowing.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyBuildingTemplate = BuildingTemplate<any>

/** Building template interface */
export interface BuildingTemplate<TParams extends BaseTemplateParams = BaseTemplateParams> {
  /** Unique template identifier */
  id: TemplateType

  /** Display name */
  name: string

  /** Short description */
  description: string

  /** Icon for UI display */
  icon: LucideIcon

  /** Building type this template creates */
  buildingType: HangarType

  /** Generate building configuration from parameters */
  generateBuilding: (params: TParams) => GeneratedBuilding

  /** Generate units for the building */
  generateUnits: (building: GeneratedBuilding, params: TParams) => GeneratedUnit[]

  /** Zod schema for form validation */
  formSchema: z.ZodSchema<TParams>

  /** Default parameter values */
  defaultValues: TParams
}

/** Template metadata for display in selector */
export interface TemplateInfo {
  id: TemplateType
  name: string
  description: string
  icon: LucideIcon
  buildingType: HangarType
  available: boolean
}
